import { useContext, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
	FilterModel,
	UnifiedSearchRequestModel,
	UnifiedSearchResultModel,
	Client as SearchClient,
} from '@bizzkit/ecommerce-search/client';
import {
	InfiniteData,
	QueryClient,
	UseInfiniteQueryResult,
	useInfiniteQuery,
	useQueryClient,
} from '@tanstack/react-query';
import type { AxiosRequestConfig } from 'axios';
import { queryKeys } from '@/api/apiConfig';
import { searchCategoriesServerSideFetch } from '@/api/product/searchCategories';
import { Filter, FilterType, ProductSort, SortDirection } from '@/api/product/types';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import { Area, PageType, ProductCategoryId, SearchSettingsResponse, SegmentationId } from '@/generated/data.schemas';
import { prefetchGetProductCategory } from '@/generated/product-category';

export interface ProductListPageQuery {
	segmentationId?: number;
	phrase?: string;
	minPrice?: number;
	maxPrice?: number;
	pageSize?: number;
	sortBy?: string;
	sortDirection?: string;
	productCategoryId?: string;
	filters?: Filter[];
}

const ValueSeparator = '--';
const FromSuffix = '.from';
const ToSuffix = '.to';
const MaxTake = 128;
const MaxOffset = 872;
const DefaultSortBy = ProductSort.Score;
const DefaultSortDirection = SortDirection.Descending;
export const DefaultPageSize = 28;

export const searchProducts = async (
	query: ProductListPageQuery,
	page: number,
	settings?: SearchSettingsResponse,
	skipPriceFormatting = false,
): Promise<UnifiedSearchResultModel> => {
	if (!settings) throw new Error('Search settings are not available.');

	const searchClient = new SearchClient({ baseUrl: settings.searchApiHost });
	const scopeId = query.phrase?.length ? settings.fullSearchScopeId : settings.browseScopeId;
	const take = query.pageSize || DefaultPageSize;
	const skip = Math.max((page - 1) * take, 0);

	const request: UnifiedSearchRequestModel = {
		segmentId: settings.searchSegment,
		scopeId: scopeId,
		authenticationToken: settings.searchToken,
		offsetProducts: skip < MaxOffset ? skip : MaxOffset,
		numberOfProducts: take <= MaxTake ? take : MaxTake,
	};

	const filters: Record<string, FilterModel> = {};
	query.filters?.forEach((f) => {
		if (f.type === FilterType.Number) {
			if (filters[f.id] !== undefined) {
				filters[f.id].from = f.min;
				filters[f.id].to = f.max;
			} else {
				filters[f.id] = { from: f.min, to: f.max };
			}
		} else if (f.type === FilterType.String) {
			if (!f.values?.length) return;
			if (filters[f.id] !== undefined) {
				f.values.forEach((v) => filters[f.id].values?.push(v.value));
			} else {
				filters[f.id] = { values: f.values.map((v) => v.value) };
			}
		}
	});
	if (query.minPrice !== undefined && query.maxPrice !== undefined) {
		filters[settings.priceFilterId] = {
			from: skipPriceFormatting ? query.minPrice : query.minPrice * 100,
			to: skipPriceFormatting ? query.maxPrice : query.maxPrice * 100,
		};
	}
	if (query.productCategoryId) {
		filters[settings.categoryIdsFilterId] = { values: [query.productCategoryId] };
	}
	if (Object.keys(filters).length > 0) {
		request.filters = filters;
	}

	if (query.phrase?.length) {
		request.phrase = query.phrase;
	}

	function mapSortBy(sortBy: string | undefined): string {
		switch (sortBy) {
			case ProductSort.Price:
				return 'price';
			case ProductSort.Name:
				return 'name';
			case ProductSort.Score:
				return 'score';
			default:
				return 'score';
		}
	}
	if (query.sortBy) {
		const sortBy = mapSortBy(query.sortBy);
		const sortDirection = query.sortDirection === SortDirection.Ascending ? 'asc' : 'desc';
		request.sort = [{ key: sortBy, order: sortDirection }];
	}

	const result = await searchClient.search.search(request);

	return result.data;
};

export const parseQueryParameters = (
	segmentationId: SegmentationId,
	searchParams: URLSearchParams,
	productCategoryId?: ProductCategoryId,
): ProductListPageQuery => {
	const filters = [] as Filter[];
	const predefinedKeys = ['phrase', 'page', 'pageSize', 'sortBy', 'sortDirection', 'minPrice', 'maxPrice'];
	searchParams.forEach(function (value, key) {
		if (predefinedKeys.some((k) => k === key)) return;
		if (key.endsWith(ToSuffix)) {
			const id = key.replace(ToSuffix, '');
			const match = filters.find((f) => f.id === id);
			if (match) {
				match.max = Number(value);
			} else {
				filters.push({ id, type: FilterType.Number, min: undefined, max: Number(value) });
			}
		} else if (key.endsWith(FromSuffix)) {
			const id = key.replace(FromSuffix, '');
			const match = filters.find((f) => f.id === id);
			if (match) {
				match.min = Number(value);
			} else {
				filters.push({ id, type: FilterType.Number, min: Number(value), max: undefined });
			}
		} else {
			filters.push({
				id: key,
				type: FilterType.String,
				values: value.split(ValueSeparator).map((v) => {
					return { label: key, value: v, isSelected: true, hitCount: 0 };
				}),
			});
		}
	});
	return {
		segmentationId: segmentationId,
		productCategoryId: productCategoryId,
		phrase: searchParams.get('phrase') || undefined,
		pageSize: Number(searchParams.get('pageSize')) || undefined,
		sortBy: searchParams.get('sortBy') || DefaultSortBy,
		sortDirection: searchParams.get('sortDirection') || DefaultSortDirection,
		filters: filters,
		minPrice: Number(searchParams.get('minPrice')) || undefined,
		maxPrice: Number(searchParams.get('maxPrice')) || undefined,
	};
};

export const useProductSearchFilters = (
	productCategoryId?: ProductCategoryId,
): [
	ProductListPageQuery,
	(toBeSet?: Filter[], toBeRemoved?: Filter[]) => void,
	(minPrice?: number, maxPrice?: number) => void,
	(sortBy?: string, sortDirection?: string) => void,
	(page?: number, pageSize?: number) => void,
	() => void,
	number | undefined,
] => {
	const [searchParams, setSearchParams] = useSearchParams();
	const { segmentationId } = useContext(SegmentationContext);
	const currentFilter = parseQueryParameters(segmentationId, searchParams, productCategoryId);
	const currentPage = Number(searchParams.get('page')) || undefined;
	const route =
		searchParams.get('phrase') && !productCategoryId
			? { area: Area.StaticPages, pageType: PageType.Search }
			: {
					area: Area.ProductCategory,
					productCategoryId: productCategoryId,
				};

	// IMPORTANT these functions must only run once on a page or else it replaces the browser history

	const setPriceFilter = (minPrice?: number, maxPrice?: number): void => {
		setSearchParams(
			(params) => {
				if (minPrice) params.set('minPrice', minPrice.toString());
				else params.delete('minPrice');
				if (maxPrice) params.set('maxPrice', maxPrice.toString());
				else params.delete('maxPrice');
				return params;
			},
			{ state: route },
		);
	};

	const setProductSearchFilters = (toBeSet?: Filter[], toBeRemoved?: Filter[]): void => {
		if ((!toBeSet || toBeSet.length === 0) && (!toBeRemoved || toBeRemoved.length === 0)) return;
		setSearchParams(
			(params) => {
				if (toBeRemoved) {
					toBeRemoved.forEach((filter) => {
						if (filter.type === FilterType.String) {
							const current = params.get(filter.id);
							if (current) {
								const values = current.split(ValueSeparator);
								const newValues = values.filter((v) => !filter.values?.some((f) => f.value === v));
								if (newValues.length > 0) {
									params.set(
										filter.id,
										newValues.reduce((p, v) => `${p}${ValueSeparator}${v}`),
									);
								} else {
									params.delete(filter.id);
								}
							}
						} else if (filter.type === FilterType.Number) {
							params.delete(filter.id + FromSuffix);
							params.delete(filter.id + ToSuffix);
						}
					});
				}
				if (toBeSet) {
					toBeSet.forEach((filter) => {
						const current = params.get(filter.id);
						if (current) {
							if (filter.type === FilterType.String && !!filter.values?.length) {
								params.set(
									filter.id,
									filter.values.map((v) => v.value).reduce((p, v) => `${p}${ValueSeparator}${v}`),
								);
							} else if (filter.type === FilterType.Number) {
								if (filter.min) {
									params.set(filter.id + FromSuffix, filter.min.toString());
								}
								if (filter.max) {
									params.set(filter.id + ToSuffix, filter.max.toString());
								}
							}
						} else {
							if (filter.type === FilterType.String && !!filter.values?.length) {
								params.append(
									filter.id,
									filter.values.map((v) => v.value).reduce((p, v) => `${p}${ValueSeparator}${v}`),
								);
							} else if (filter.type === FilterType.Number) {
								if (filter.min) {
									params.append(filter.id + FromSuffix, filter.min.toString());
								}
								if (filter.max) {
									params.append(filter.id + ToSuffix, filter.max.toString());
								}
							}
						}
					});
				}
				params.delete('page'); // remove page
				return params;
			},
			{ state: route },
		);
	};

	const setProductSort = (sortBy?: string, sortDirection?: string): void => {
		setSearchParams(
			(params) => {
				if (sortBy) params.set('sortBy', sortBy);
				else params.delete('sortBy');
				if (sortDirection) params.set('sortDirection', sortDirection);
				else params.delete('sortDirection');

				params.delete('page'); // remove page
				return params;
			},
			{ state: route },
		);
	};

	const setProductPage = (page?: number, pageSize?: number): void => {
		setSearchParams(
			(params) => {
				if (page) params.set('page', page.toString());
				else params.delete('page');
				if (pageSize) params.set('pageSize', pageSize.toString());
				else params.delete('pageSize');
				return params;
			},
			{ state: { ...route, preventScrollTo: true } },
		);
	};

	const resetFilters = (): void => {
		setSearchParams(
			(params) => {
				const oldPhrase = params.get('phrase');
				const oldSortBy = params.get('sortBy');
				const oldSortDirection = params.get('sortDirection');

				const newParams = new URLSearchParams();

				if (oldPhrase) newParams.set('phrase', oldPhrase);
				if (oldSortBy) newParams.set('sortBy', oldSortBy);
				if (oldSortDirection) newParams.set('sortDirection', oldSortDirection);

				return newParams;
			},
			{ state: route },
		);
	};

	return [
		currentFilter,
		setProductSearchFilters,
		setPriceFilter,
		setProductSort,
		setProductPage,
		resetFilters,
		currentPage,
	];
};

export const productSearchServerSideFetch = async (
	queryClient: QueryClient,
	segmentationId: number,
	productCategoryId?: ProductCategoryId,
	queryString?: string,
	settings?: SearchSettingsResponse,
	axiosRequestConfig?: AxiosRequestConfig,
): Promise<void> => {
	const searchParams = new URLSearchParams(queryString);
	const query = parseQueryParameters(segmentationId, searchParams, productCategoryId);
	const page = Number(searchParams.get('page')) || undefined;

	if (productCategoryId) {
		await prefetchGetProductCategory(queryClient, segmentationId, productCategoryId, { axios: axiosRequestConfig });
	}

	if (query.phrase?.length && settings !== undefined) {
		await searchCategoriesServerSideFetch(queryClient, settings, query.phrase);
	}

	return queryClient.prefetchInfiniteQuery({
		// page should not be part of the key because react-query controls it internally
		queryKey: queryKeys.product.search(query, settings).queryKey,
		queryFn: ({ pageParam }) => {
			return searchProducts(query, pageParam, settings);
		},
		initialPageParam: page || 1,
	});
};

export const useProductSearchQuery = (
	productCategoryId?: ProductCategoryId,
	settings?: SearchSettingsResponse,
): UseInfiniteQueryResult<InfiniteData<UnifiedSearchResultModel>> => {
	const [query, , , , , , currentPage] = useProductSearchFilters(productCategoryId);
	const queryClient = useQueryClient();

	const queryKey = queryKeys.product.search(query, settings).queryKey;

	// If we come back in here without a page specified and the cached data has multiple pages or
	// starts after page 1, then we remove the query from the cache.
	useEffect(() => {
		if (!currentPage) {
			const data: InfiniteData<UnifiedSearchResultModel> | undefined = queryClient.getQueryData(queryKey);
			if (
				data?.pages &&
				data.pages.length > 0 &&
				(data.pages.length > 1 || (data.pages[0].productOffset || 0) > 0)
			)
				queryClient.removeQueries({ queryKey: queryKey });
		}
		// Cannot rely on queryKey being a stable reference
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentPage, productCategoryId, queryClient]);

	// We can't use a select here to map the response until v5 of react-query:
	// https://github.com/TanStack/query/issues/3065#issuecomment-1445431759
	return useInfiniteQuery({
		// currentPage should not be part of the key because react-query controls it internally
		// Disabling since ESLint thinks that query is not part of queryKey
		// eslint-disable-next-line @tanstack/query/exhaustive-deps
		queryKey: queryKey,
		queryFn: ({ pageParam }) => {
			return searchProducts(query, pageParam, settings);
		},
		initialPageParam: currentPage || 1,
		getNextPageParam: (lastPage) => {
			const numberOfPages = Math.ceil((lastPage.totalProducts || 0) / (query.pageSize || DefaultPageSize));
			const page = (lastPage.productOffset || 0) / (query.pageSize || DefaultPageSize) + 1;
			const hasMorePages = page < numberOfPages;
			return hasMorePages ? page + 1 : undefined;
		},
		getPreviousPageParam: (firstPage) => {
			const page = (firstPage.productOffset || 0) / (query.pageSize || DefaultPageSize) + 1;
			return page > 1 ? page - 1 : undefined;
		},
		retry: false,
	});
};
